import React, { useMemo } from 'react';
import type { FormField } from '../interfaces';
import { CircularProgress, TextField } from '@material-ui/core';
import { useField, useFormikContext } from 'formik';
import { Autocomplete } from '@material-ui/lab';
import classNames from 'classnames';
import type { DokumentDto } from '../services/rtkApi';
import { useGetDokumenterQuery } from '../services/rtkApi';
import { getLocalizedLabel } from '../utils';
import { getFormFieldStyles } from '../formFieldStyles';

type GarantiDokumentSelectorProps = {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
};

export const GarantiDokumentSelect = ({
    field,
    isReadOnly,
    isRequired,
}: GarantiDokumentSelectorProps) => {
    const classes = getFormFieldStyles();
    const [formikField, meta] = useField<string | undefined>(field.id);
    const { setFieldValue } = useFormikContext();
    const { data, isLoading, isFetching } = useGetDokumenterQuery(undefined, {
        skip: isReadOnly,
    });

    const selectedValue = useMemo(
        () =>
            formikField.value
                ? (JSON.parse(formikField.value) as DokumentDto)
                : null,
        [formikField.value],
    );

    const handleChange = (value: DokumentDto | null) => {
        const fieldString = value ? JSON.stringify(value) : '';
        setFieldValue(field.id, fieldString);
    };
    const isLoadingOrFetching = isLoading || isFetching;

    return (
        <Autocomplete<DokumentDto>
            id="garanti-dokument-select"
            selectOnFocus={true}
            clearOnBlur={true}
            handleHomeEndKeys={true}
            disabled={isReadOnly}
            options={data || []}
            getOptionLabel={(option) => `${option.navn} (${option.filnavn})`}
            getOptionSelected={(option, value) => option.id === value?.id}
            value={selectedValue}
            onChange={(_event, newValue) => handleChange(newValue)}
            loading={isLoadingOrFetching}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth={true}
                    className={classNames({
                        [classes.editableField]: !isReadOnly,
                        [classes.readOnlyField]: isReadOnly,
                    })}
                    required={isRequired}
                    variant="outlined"
                    disabled={isReadOnly}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoadingOrFetching && (
                                    <CircularProgress color="inherit" />
                                )}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    label={getLocalizedLabel(
                        `project.formFields.${field?.id}`,
                        field.label,
                        field.id,
                    )}
                    helperText={meta.touched ? meta.error : ''}
                    error={meta.touched && Boolean(meta.error)}
                />
            )}
        />
    );
};
